/**
 * What the models cost this session. Token counts come from the provider, prices from the dated
 * table the voice service serves (`voice/pricing.json`). Everything here is an estimate from those
 * two things — it is not a bill, and it says so wherever it is shown.
 */

export interface ModelUsage {
  inputTokens?: number;
  cachedInputTokens?: number;
  outputTokens?: number;
  /** Already counted inside outputTokens and billed there; kept only so the debrief can show it. */
  reasoningTokens?: number;
  audioInputTokens?: number;
  cachedAudioInputTokens?: number;
  audioOutputTokens?: number;
  model?: string;
  latencyMs?: number;
}

export type CostFeature = 'brain' | 'interpret' | 'speech' | 'radio';

export interface ModelPrice {
  inputPerM: number;
  cachedInputPerM: number;
  outputPerM: number;
  audioInputPerM?: number;
  cachedAudioInputPerM?: number;
  audioOutputPerM?: number;
}

export interface PriceTable {
  asOf: string;
  currency?: string;
  models: Record<string, ModelPrice>;
  tts?: { model: string; charactersPerToken: number };
}

export interface FeatureTotals {
  calls: number;
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  audioTokens: number;
  usd: number;
  avgLatencyMs: number;
  p95LatencyMs: number;
}

export interface CostSummary {
  asOf: string;
  priced: boolean;
  totalUsd: number;
  perHourUsd: number;
  elapsedS: number;
  features: Record<CostFeature, FeatureTotals>;
}

export interface CostMeter {
  record(feature: CostFeature, usage: ModelUsage): void;
  /** Speech is billed per character of text; the table says how many characters make a token. */
  recordSpeech(characters: number, model?: string): void;
  summary(elapsedS: number): CostSummary;
  setPricing(pricing: PriceTable | null): void;
}

const empty = (): FeatureTotals & { latencies: number[] } => ({ calls: 0, inputTokens: 0, cachedInputTokens: 0, outputTokens: 0, audioTokens: 0, usd: 0, avgLatencyMs: 0, p95LatencyMs: 0, latencies: [] });
const round = (n: number, places = 4) => Math.round(n * 10 ** places) / 10 ** places;

/** Must stay identical to `priceOf` in voice/pricing.mjs; voice/cost-cases.json holds both to it. */
export function costOf(usage: ModelUsage, pricing: PriceTable | null): number {
  const price = pricing?.models?.[usage.model ?? ''] ?? (usage.model ? undefined : pricing?.models?.local);
  if (!price) return 0;
  const per = (tokens: number | undefined, rate: number | undefined) => ((tokens ?? 0) * (rate ?? 0)) / 1e6;
  return per(usage.inputTokens, price.inputPerM)
    + per(usage.cachedInputTokens, price.cachedInputPerM)
    // Reasoning tokens are already inside outputTokens; charging them again would double count.
    + per(usage.outputTokens, price.outputPerM)
    + per(usage.audioInputTokens, price.audioInputPerM)
    + per(usage.cachedAudioInputTokens, price.cachedAudioInputPerM)
    + per(usage.audioOutputTokens, price.audioOutputPerM);
}

export function createCostMeter(pricing: PriceTable | null = null): CostMeter {
  const totals: Record<CostFeature, ReturnType<typeof empty>> = { brain: empty(), interpret: empty(), speech: empty(), radio: empty() };
  let table = pricing;

  const add = (feature: CostFeature, usage: ModelUsage) => {
    const t = totals[feature];
    t.calls++;
    t.inputTokens += usage.inputTokens ?? 0;
    t.cachedInputTokens += usage.cachedInputTokens ?? 0;
    t.outputTokens += usage.outputTokens ?? 0;
    t.audioTokens += (usage.audioInputTokens ?? 0) + (usage.cachedAudioInputTokens ?? 0) + (usage.audioOutputTokens ?? 0);
    t.usd += costOf(usage, table);
    if (typeof usage.latencyMs === 'number') { t.latencies.push(usage.latencyMs); if (t.latencies.length > 500) t.latencies.shift(); }
  };

  return {
    record(feature, usage) { add(feature, usage); },
    recordSpeech(characters, model) {
      const perToken = table?.tts?.charactersPerToken ?? 4;
      add('speech', { model: model ?? table?.tts?.model, outputTokens: Math.ceil(characters / perToken) });
    },
    setPricing(next) { table = next; },
    summary(elapsedS) {
      const features = {} as Record<CostFeature, FeatureTotals>;
      let totalUsd = 0;
      for (const key of Object.keys(totals) as CostFeature[]) {
        const t = totals[key];
        const sorted = [...t.latencies].sort((a, b) => a - b);
        features[key] = {
          calls: t.calls, inputTokens: t.inputTokens, cachedInputTokens: t.cachedInputTokens, outputTokens: t.outputTokens, audioTokens: t.audioTokens,
          usd: round(t.usd),
          avgLatencyMs: sorted.length ? Math.round(sorted.reduce((s, v) => s + v, 0) / sorted.length) : 0,
          p95LatencyMs: sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))]! : 0,
        };
        totalUsd += t.usd;
      }
      const hours = Math.max(elapsedS, 1) / 3600;
      return { asOf: table?.asOf ?? '', priced: !!table, totalUsd: round(totalUsd), perHourUsd: round(totalUsd / hours), elapsedS: Math.round(elapsedS), features };
    },
  };
}

/** The price table the voice service serves; null when it is not running. */
export async function loadPricing(fetchFn: typeof fetch = fetch): Promise<PriceTable | null> {
  try {
    const res = await fetchFn('/api/voice/pricing', { headers: { 'x-atc-voice': '1' } });
    if (!res.ok) return null;
    const table = await res.json();
    return table && typeof table === 'object' && table.models ? (table as PriceTable) : null;
  } catch {
    return null;
  }
}
